import {
  EntityLinks,
  Goal,
  GoalProgressCount,
  GoalSortingMode,
  ID,
  OrderDirection,
} from '@griply/types';

import { getOrderedGoalIds } from '../get-ordered-goal-ids';
import { sortGoals } from '../sort-goals';
import { getPresentParentIds } from './get-present-parent-ids';

export type GetGoalFolderChildrenOptions = {
  links: EntityLinks;
  // A goal whose parents are all absent nests here; without it such goals are roots.
  rootGoalId?: ID;
  // Absent or Custom ranks by the parent's goalSorting; a value mode ignores it.
  sortMode?: GoalSortingMode;
  sortDirection?: OrderDirection;
  allGoals?: Goal[];
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
};

// Code-unit order (not localeCompare) so V8 and Hermes agree on ties.
const byCreatedAtThenId = (goalA: Goal, goalB: Goal): number => {
  const createdDiff = goalA.createdAt.getTime() - goalB.createdAt.getTime();
  if (createdDiff) {
    return createdDiff;
  }
  if (goalA.id === goalB.id) {
    return 0;
  }
  return goalA.id < goalB.id ? -1 : 1;
};

const sortByParentOrder = (children: Goal[], parent?: Goal): ID[] => {
  const order = new Map(
    (parent?.goalSorting ?? []).map((id, index) => [id, index]),
  );

  return children
    .slice()
    .sort((goalA, goalB) => {
      const indexA = order.get(goalA.id);
      const indexB = order.get(goalB.id);
      if (indexA !== undefined && indexB !== undefined) {
        return indexA - indexB;
      }
      if (indexA !== undefined) {
        return -1;
      }
      if (indexB !== undefined) {
        return 1;
      }
      return byCreatedAtThenId(goalA, goalB);
    })
    .map(({ id }) => id);
};

const sortChildren = (
  children: Goal[],
  parent: Goal | undefined,
  goals: Goal[],
  {
    sortMode,
    sortDirection = OrderDirection.Asc,
    allGoals = goals,
    taskCounts,
    goalProgressCounts,
    links,
  }: GetGoalFolderChildrenOptions,
): ID[] => {
  if (!sortMode || sortMode === GoalSortingMode.Custom) {
    const ordered = sortByParentOrder(children, parent);
    return sortDirection === OrderDirection.Desc ? ordered.reverse() : ordered;
  }

  return getOrderedGoalIds(
    sortGoals(children, {
      allGoals,
      sortMode,
      taskCounts,
      goalProgressCounts,
      links,
    }),
    sortMode,
    sortDirection,
  );
};

export const getGoalFolderChildren = (
  goals: Goal[],
  options: GetGoalFolderChildrenOptions,
): Map<ID, ID[]> => {
  const { links, rootGoalId } = options;
  const goalsById = new Map(goals.map((goal) => [goal.id, goal]));
  const isPresent = (id: ID) => goalsById.has(id);
  const childrenByParentId = new Map<ID, Goal[]>();

  goals.forEach((goal) => {
    if (goal.id === rootGoalId) {
      return;
    }

    const presentParentIds = getPresentParentIds(goal, isPresent, {
      links,
      goalsById,
    });
    const parentIds = presentParentIds.length
      ? presentParentIds
      : [rootGoalId].filter((id): id is ID => !!id);

    parentIds.forEach((parentId) => {
      const siblings = childrenByParentId.get(parentId) ?? [];
      siblings.push(goal);
      childrenByParentId.set(parentId, siblings);
    });
  });

  return new Map(
    Array.from(childrenByParentId, ([parentId, children]) => [
      parentId,
      sortChildren(children, goalsById.get(parentId), goals, options),
    ]),
  );
};
